import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api, ApiError } from '@/lib/api'
import { useAuthStore, type AuthUser } from '@/stores/auth'

/**
 * `useCurrentUser` — fetches `GET /admin/auth/me` and keeps the Zustand
 * auth store synced with the latest server-side projection (name / role /
 * active flag may have changed between login and now).
 *
 * The 401 interceptor in `lib/api.ts` already clears the store and bounces
 * to `/login` on session expiry — this hook just surfaces the same data
 * for display.
 *
 * Story 1.7 §AC #12 + §AC #14.
 */
interface MeResponse {
  user: AuthUser & {
    active: boolean
    lastLoginAt: string | null
  }
}

export function useCurrentUser(): {
  user: MeResponse['user'] | null
  isLoading: boolean
  isError: boolean
} {
  const token = useAuthStore((s) => s.token)
  const setAuth = useAuthStore((s) => s.setAuth)

  const query = useQuery<MeResponse, ApiError>({
    queryKey: ['admin', 'auth', 'me'],
    enabled: token !== null && token !== '',
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<MeResponse> => {
      return await api<MeResponse>('/admin/auth/me')
    },
  })

  // Whenever the server-side projection updates, mirror the changes back
  // into the auth store so AdminLayout / Topbar pick them up.
  useEffect(() => {
    if (query.data && token) {
      const u = query.data.user
      setAuth({ id: u.id, name: u.name, email: u.email, role: u.role }, token)
    }
  }, [query.data, setAuth, token])

  return {
    user: query.data?.user ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}
